import React, { useState, useEffect } from 'react';
import { X, Mail, Loader, Download, Lock, Check } from 'lucide-react';
import { scanGmailForTravelDocs, downloadGmailAttachment, ImportableDocument } from '@/lib/gmailService';

interface GmailImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  accessToken: string | null;
  onImport: (filename: string, content: string, password?: string) => Promise<void>;
}

export const GmailImportModal: React.FC<GmailImportModalProps> = ({
  isOpen,
  onClose,
  accessToken,
  onImport
}) => {
  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState<ImportableDocument[]>([]);
  const [selectedDocs, setSelectedDocs] = useState<Set<string>>(new Set());
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [protectWithPassword, setProtectWithPassword] = useState(false);
  const [password, setPassword] = useState('');

  useEffect(() => {
    if (isOpen && accessToken) {
      scanInbox();
    }
  }, [isOpen, accessToken]);

  const scanInbox = async () => {
    if (!accessToken) {
      setError('Gmail access not granted. Please log out and log back in with Google to grant Gmail permissions.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const found = await scanGmailForTravelDocs(accessToken);
      setDocuments(found);
    } catch (err) {
      console.error('Failed to scan Gmail:', err);
      setError('Failed to scan Gmail inbox. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelection = (attachmentId: string) => {
    const newSelected = new Set(selectedDocs);
    if (newSelected.has(attachmentId)) {
      newSelected.delete(attachmentId);
    } else {
      newSelected.add(attachmentId);
    }
    setSelectedDocs(newSelected);
  };

  const handleImport = async () => {
    if (!accessToken || selectedDocs.size === 0) return;

    setImporting(true);
    setError(null);

    try {
      for (const attachmentId of selectedDocs) {
        const doc = documents.find(d => d.attachmentId === attachmentId);
        if (!doc) continue;

        // Download attachment
        const content = await downloadGmailAttachment(accessToken, doc.messageId, doc.attachmentId);

        // Import to wallet
        await onImport(doc.filename, content, protectWithPassword ? password : undefined);
      }

      alert(`Successfully imported ${selectedDocs.size} document(s) to your wallet!`);
      onClose();
    } catch (err) {
      console.error('Import failed:', err);
      setError('Failed to import documents. Please try again.');
    } finally {
      setImporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="w-full max-w-4xl max-h-[90vh] bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-3xl shadow-2xl overflow-hidden border-2 border-blue-400/30">
        
        {/* Header */}
        <div className="relative bg-gradient-to-r from-blue-600 via-blue-500 to-blue-600 p-6 text-center">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full bg-black/20 hover:bg-black/40 text-white transition-colors z-10"
          >
            <X className="w-5 h-5" />
          </button>
          
          <Mail className="w-12 h-12 mx-auto mb-3 text-white" />
          <h1 className="text-3xl font-bold text-white">
            Import from Gmail
          </h1>
          <p className="text-blue-100 text-sm mt-2">
            Scan your inbox for boarding passes, hotels, and travel documents
          </p>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-250px)]">
          {!accessToken ? (
            <div className="text-center py-16">
              <Mail className="w-16 h-16 text-amber-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-3">Gmail Access Required</h3>
              <p className="text-slate-300 mb-6 max-w-md mx-auto">
                To import boarding passes from your Gmail, you need to grant Gmail access permissions.
              </p>
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 mb-6 max-w-md mx-auto">
                <p className="text-sm text-blue-200">
                  <strong>Steps to enable:</strong>
                </p>
                <ol className="text-sm text-blue-300 text-left mt-2 space-y-2 list-decimal list-inside">
                  <li>Log out of WHIPT</li>
                  <li>Log back in with Google</li>
                  <li>Grant "View email" permission when prompted</li>
                  <li>Return here and click Import from Gmail</li>
                </ol>
              </div>
              <button
                onClick={onClose}
                className="px-6 py-2 rounded-full bg-slate-700 hover:bg-slate-600 text-white transition-colors"
              >
                Got it
              </button>
            </div>
          ) : error ? (
            <div className="mb-4 p-4 rounded-xl bg-red-500/20 border border-red-500/50 text-red-200">
              {error}
            </div>
          ) : null}

          {accessToken && !error && (
            loading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <Loader className="w-12 h-12 text-blue-400 animate-spin mb-4" />
                <p className="text-slate-300">Scanning your inbox...</p>
              </div>
            ) : documents.length === 0 ? (
            <div className="text-center py-16">
              <Mail className="w-16 h-16 text-slate-500 mx-auto mb-4" />
              <p className="text-slate-400 text-lg">No travel documents found</p>
              <p className="text-slate-500 text-sm mt-2">
                We'll keep looking for boarding passes, hotel confirmations, and more
              </p>
              <button
                onClick={scanInbox}
                className="mt-6 px-6 py-2 rounded-full bg-blue-500 hover:bg-blue-600 text-white transition-colors"
              >
                Scan Again
              </button>
            </div>
          ) : (
            <>
              <div className="mb-6 flex items-center justify-between">
                <p className="text-slate-300">
                  Found <span className="font-bold text-blue-400">{documents.length}</span> documents
                </p>
                <button
                  onClick={() => {
                    if (selectedDocs.size === documents.length) {
                      setSelectedDocs(new Set());
                    } else {
                      setSelectedDocs(new Set(documents.map(d => d.attachmentId)));
                    }
                  }}
                  className="text-sm text-blue-400 hover:text-blue-300"
                >
                  {selectedDocs.size === documents.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>

              <div className="space-y-3">
                {documents.map((doc) => {
                  const isSelected = selectedDocs.has(doc.attachmentId);
                  
                  return (
                    <div
                      key={doc.attachmentId}
                      onClick={() => toggleSelection(doc.attachmentId)}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        isSelected
                          ? 'border-blue-500 bg-blue-500/10'
                          : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-1 ${
                          isSelected ? 'border-blue-500 bg-blue-500' : 'border-slate-600'
                        }`}>
                          {isSelected && <Check className="w-4 h-4 text-white" />}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-white truncate">{doc.filename}</p>
                          <p className="text-sm text-slate-400 truncate mt-1">{doc.subject}</p>
                          <div className="flex items-center gap-4 mt-2">
                            <span className="text-xs text-slate-500">From: {doc.from.split('<')[0].trim()}</span>
                            <span className="text-xs text-slate-500">
                              {new Date(doc.date).toLocaleDateString()}
                            </span>
                            <span className="text-xs px-2 py-1 rounded-full bg-blue-500/20 text-blue-300 capitalize">
                              {doc.category.replace('-', ' ')}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
            )
          )}
        </div>

        {/* Footer */}
        {documents.length > 0 && (
          <div className="border-t border-slate-700 p-6 space-y-4">
            {/* Password Protection Option */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="protect-gmail"
                checked={protectWithPassword}
                onChange={(e) => setProtectWithPassword(e.target.checked)}
                className="w-4 h-4 rounded border-slate-600 text-blue-500 focus:ring-blue-500"
              />
              <label htmlFor="protect-gmail" className="text-sm text-slate-300 flex items-center gap-2">
                <Lock className="w-4 h-4" />
                Encrypt imported documents with password
              </label>
            </div>

            {protectWithPassword && (
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter encryption password"
                className="w-full px-4 py-2 rounded-xl bg-slate-800 border border-slate-600 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            )}

            <div className="flex gap-3">
              <button
                onClick={onClose}
                disabled={importing}
                className="flex-1 py-3 px-4 rounded-xl bg-slate-700 hover:bg-slate-600 text-white transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleImport}
                disabled={selectedDocs.size === 0 || importing || (protectWithPassword && !password)}
                className="flex-1 py-3 px-4 rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {importing ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Download className="w-5 h-5" />
                    Import {selectedDocs.size > 0 && `(${selectedDocs.size})`}
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
